import {
    Colors,
    Icon,
    type CheckboxGroupOption,
    type DraggableItem,
    isIconName,
} from '@amelio/ui-library';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { AddCriteriaModal } from './components/AddCriteriaModal/AddCriteriaModal';
import { SelectEvaluators } from './components/SelectEvaluators';
import { buildCycleEvaluatorCriterias, getCriteriaType } from './evaluatorSelection';
import { useAutosaveEvaluatorSelection } from './useAutosaveEvaluatorSelection';
import {
    SelectEvaluatorsFormFields,
    selectEvaluatorsSchema,
    type SelectEvaluatorsFormData,
} from './validation';

import type { TranslationKey } from '@/i18n-key';
import type { CycleEvaluationFormNavigatorParams } from '@/navigation/CycleEvaluation/CycleEvaluationFormNavigator';
import type { CycleEvaluationGroupFormNavigatorParams } from '@/navigation/CycleEvaluation/CycleEvaluationGroupFormNavigator';
import type { PriorityAttribute } from '@/types/priorityAttributeType';

import { useGetCycleEvaluation } from '@/api/cycle-evaluation-services/cycle-evaluation';
import { useGetCycleEvaluationGroup } from '@/api/cycle-evaluation-services/cycle-evaluation-group';
import { useUpdateCycleEvaluationGroup } from '@/api/cycle-evaluation-services/cycle-evaluation-group/cycleEvaluationGroupMutations';
import { useUpdateCycleEvaluatorSelection } from '@/api/cycle-evaluation-services/cycle-evaluator-selection';
import { useGetCompanyInformation } from '@/api/directory';
import { feedback360SettingsOnNextAtom, selectEvaluatorsOnNextAtom } from '@/atoms/cycleEvaluation';
import { EvaluationType } from '@/constants/cycle-evaluation/CycleEvaluation';
import { CycleEvaluationGroupSteps } from '@/constants/cycle-evaluation/CycleEvaluationGroupSteps';
import { useRegisterPageAction, useAmelioTranslations } from '@/hooks';
import { useFetchErrorsHandler } from '@/hooks/api/useFetchErrorsHandler';
import { useError } from '@/hooks/utils/useError';
import { isCycleEvaluationGroupLaunched } from '@/navigation/CycleEvaluation/cycleEvaluationGroupLaunched';
import { isCycleEvaluationGroupReadOnly } from '@/navigation/CycleEvaluation/cycleEvaluationGroupReadOnly';
import {
    buildCycleEvaluationGroupUpdatePayload,
    findGroupEvaluationByType,
} from '@/navigation/CycleEvaluation/cycleEvaluationGroupSteps';
import { getCompanyAttributeList } from '@/utils/company';
import { getPriorityCriteria, priorityTypeToFilterKey } from '@/utils/cycleEvaluation';
import { validationMessageKey } from '@/utils/validationMessage';

const PRIORITY_FIELD = {
    1: SelectEvaluatorsFormFields.priority1,
    2: SelectEvaluatorsFormFields.priority2,
    3: SelectEvaluatorsFormFields.priority3,
} as const;

const CRITERION_LABEL_KEYS: Record<string, TranslationKey> = {
    SameJobTitle: 'cycleEvaluationSelectEvaluators.criterion.sameJobTitle',
    SameDepartment: 'cycleEvaluationSelectEvaluators.criterion.sameDepartment',
    SameManager: 'cycleEvaluationSelectEvaluators.criterion.sameManager',
    SameOffice: 'cycleEvaluationSelectEvaluators.criterion.sameOffice',
    SameEmployeeLevel: 'cycleEvaluationSelectEvaluators.criterion.sameEmployeeLevel',
};

const hasAnyPriority = (
    priority1: DraggableItem<PriorityAttribute>[],
    priority2: DraggableItem<PriorityAttribute>[],
    priority3: DraggableItem<PriorityAttribute>[],
) => priority1.length > 0 || priority2.length > 0 || priority3.length > 0;

export const SelectEvaluatorsPage = () => {
    const { t } = useAmelioTranslations();
    const { handleError } = useError();
    const { cycleEvaluationId: routeCycleEvaluationId, cycleEvaluationGroupId } = useParams<
        Partial<CycleEvaluationFormNavigatorParams> &
            Partial<CycleEvaluationGroupFormNavigatorParams>
    >();

    const [isFormInitialized, setIsFormInitialized] = useState(false);
    const [hasUserEdited, setHasUserEdited] = useState(false);
    const [priorityToAdd, setPriorityToAdd] = useState<1 | 2 | 3>(1);
    const [isAddCriteriaOpen, setIsAddCriteriaOpen] = useState(false);
    const [priorityMessageKey, setPriorityMessageKey] = useState<TranslationKey | undefined>();
    const [countMessageKey, setCountMessageKey] = useState<TranslationKey | undefined>();

    const { data: group } = useGetCycleEvaluationGroup(cycleEvaluationGroupId);

    const isReadOnly = isCycleEvaluationGroupReadOnly(group);
    const isLaunched = isCycleEvaluationGroupLaunched(group);

    const feedback360Child = findGroupEvaluationByType(group, EvaluationType.EVALUATION_360);

    const cycleEvaluationId = cycleEvaluationGroupId
        ? feedback360Child?.id
        : routeCycleEvaluationId;

    const {
        data: cycleEvaluation,
        isError: isCycleEvaluationError,
        isLoading: isCycleEvaluationLoading,
    } = useGetCycleEvaluation(cycleEvaluationId ?? '');

    const {
        data: company,
        isError: isCompanyError,
        isLoading: isCompanyLoading,
    } = useGetCompanyInformation();

    const cycleEvaluatorSelectionId = cycleEvaluation?.cycleEvaluatorSelection?.id;

    const { mutateAsync: updateCycleEvaluatorSelection } = useUpdateCycleEvaluatorSelection();
    const { mutateAsync: updateCycleEvaluationGroup } = useUpdateCycleEvaluationGroup();

    const companyAttributeList = useMemo(() => getCompanyAttributeList(company), [company]);

    const { watch, setValue, reset, trigger, getValues, getFieldState } =
        useForm<SelectEvaluatorsFormData>({
            resolver: zodResolver(selectEvaluatorsSchema),
            defaultValues: {
                [SelectEvaluatorsFormFields.priority1]: [],
                [SelectEvaluatorsFormFields.priority2]: [],
                [SelectEvaluatorsFormFields.priority3]: [],
                [SelectEvaluatorsFormFields.suggestedReviewerCount]: 5,
            },
        });

    const formValues = watch();

    // An absent company still yields an attribute list — hydrating early freezes the labels blank.
    useEffect(() => {
        if (isFormInitialized || isCompanyLoading) return;

        if (cycleEvaluation?.cycleEvaluatorSelection && companyAttributeList) {
            const priority1 = getPriorityCriteria({
                cycleEvaluatorSelection: cycleEvaluation.cycleEvaluatorSelection,
                companyAttributeList,
                priority: 1,
            });
            const priority2 = getPriorityCriteria({
                cycleEvaluatorSelection: cycleEvaluation.cycleEvaluatorSelection,
                companyAttributeList,
                priority: 2,
            });
            const priority3 = getPriorityCriteria({
                cycleEvaluatorSelection: cycleEvaluation.cycleEvaluatorSelection,
                companyAttributeList,
                priority: 3,
            });

            reset({
                [SelectEvaluatorsFormFields.priority1]: priority1,
                [SelectEvaluatorsFormFields.priority2]: priority2,
                [SelectEvaluatorsFormFields.priority3]: priority3,
                [SelectEvaluatorsFormFields.suggestedReviewerCount]:
                    cycleEvaluation.cycleEvaluatorSelection.suggestedReviewerCount || 5,
            });
            setIsFormInitialized(true);
        }
    }, [
        cycleEvaluation?.cycleEvaluatorSelection,
        companyAttributeList,
        isCompanyLoading,
        isFormInitialized,
        reset,
    ]);

    const priority1 = formValues[SelectEvaluatorsFormFields.priority1];
    const priority2 = formValues[SelectEvaluatorsFormFields.priority2];
    const priority3 = formValues[SelectEvaluatorsFormFields.priority3];

    const criteriaIdToTypeMap = useMemo(() => {
        const map = new Map<string, string>();
        cycleEvaluation?.cycleEvaluatorSelection?.cycleEvaluatorCriterias.forEach((criteria) => {
            map.set(criteria.id, criteria.type);
        });
        return map;
    }, [cycleEvaluation?.cycleEvaluatorSelection?.cycleEvaluatorCriterias]);

    const getItemPriorityType = useCallback(
        (item: DraggableItem<PriorityAttribute>): string =>
            getCriteriaType({ item, criteriaIdToTypeMap }),
        [criteriaIdToTypeMap],
    );

    const markEdited = useCallback(() => setHasUserEdited(true), []);

    const selectedCriteria: string[] = useMemo(() => {
        return [
            ...priority1.map(getItemPriorityType),
            ...priority2.map(getItemPriorityType),
            ...priority3.map(getItemPriorityType),
        ];
    }, [priority1, priority2, priority3, getItemPriorityType]);

    const criteriaOptions = useMemo((): CheckboxGroupOption[] => {
        return Object.entries(priorityTypeToFilterKey)
            .filter(([, filterKey]) => companyAttributeList[filterKey])
            .map(([priorityType, filterKey]) => {
                const iconName =
                    priorityType === 'SameDepartment'
                        ? 'building-buildings'
                        : companyAttributeList[filterKey]?.iconName;

                return {
                    label:
                        CRITERION_LABEL_KEYS[priorityType] !== undefined
                            ? t(CRITERION_LABEL_KEYS[priorityType])
                            : companyAttributeList[filterKey]?.name || priorityType,
                    value: priorityType,
                    icon: (
                        <Icon
                            name={isIconName(iconName) ? iconName : 'essential-info-circle'}
                            size={'md'}
                            color={Colors.BASE}
                        />
                    ),
                };
            });
    }, [companyAttributeList, t]);

    const onEvaluatorsCountChange = useCallback(
        (count: number) => {
            setValue(SelectEvaluatorsFormFields.suggestedReviewerCount, count);
            markEdited();
        },
        [setValue, markEdited],
    );

    const onPriority1Change = useCallback(
        (items: DraggableItem<PriorityAttribute>[]) => {
            setValue(SelectEvaluatorsFormFields.priority1, items);
            markEdited();

            if (hasAnyPriority(items, priority2, priority3)) {
                setPriorityMessageKey(undefined);
            }
        },
        [setValue, markEdited, priority2, priority3],
    );

    const onPriority2Change = useCallback(
        (items: DraggableItem<PriorityAttribute>[]) => {
            setValue(SelectEvaluatorsFormFields.priority2, items);
            markEdited();

            if (hasAnyPriority(priority1, items, priority3)) {
                setPriorityMessageKey(undefined);
            }
        },
        [setValue, markEdited, priority1, priority3],
    );

    const onPriority3Change = useCallback(
        (items: DraggableItem<PriorityAttribute>[]) => {
            setValue(SelectEvaluatorsFormFields.priority3, items);
            markEdited();

            if (hasAnyPriority(priority1, priority2, items)) {
                setPriorityMessageKey(undefined);
            }
        },
        [setValue, markEdited, priority1, priority2],
    );

    const onAddAttribute: (priority: 1 | 2 | 3) => void = (priority) => {
        setPriorityToAdd(priority);
        setIsAddCriteriaOpen(true);
    };

    const onRemoveAttribute = useCallback(
        (priority: 1 | 2 | 3, itemId: string) => {
            const field = PRIORITY_FIELD[priority];
            const filtered = (getValues(field) as DraggableItem<PriorityAttribute>[]).filter(
                (item) => item.id !== itemId,
            );

            setValue(field, filtered);
            markEdited();

            const remainingPriority1 = priority === 1 ? filtered : priority1;
            const remainingPriority2 = priority === 2 ? filtered : priority2;
            const remainingPriority3 = priority === 3 ? filtered : priority3;

            if (hasAnyPriority(remainingPriority1, remainingPriority2, remainingPriority3)) {
                setPriorityMessageKey(undefined);
            }
        },
        [setValue, getValues, markEdited, priority1, priority2, priority3],
    );

    const onUpdateCriteria = useCallback(
        (newSelectedCriteria: string[]) => {
            const newSelectedSet = new Set(newSelectedCriteria);

            const filterBySelection = (items: DraggableItem<PriorityAttribute>[]) =>
                items.filter((item) => newSelectedSet.has(getItemPriorityType(item)));

            const filteredPriority1 = filterBySelection(priority1);
            const filteredPriority2 = filterBySelection(priority2);
            const filteredPriority3 = filterBySelection(priority3);

            const existingTypes = new Set([
                ...filteredPriority1.map(getItemPriorityType),
                ...filteredPriority2.map(getItemPriorityType),
                ...filteredPriority3.map(getItemPriorityType),
            ]);

            const newCriteriaToAdd = newSelectedCriteria.filter(
                (criteriaType) => !existingTypes.has(criteriaType),
            );

            const newItems = newCriteriaToAdd.map((criteriaType) => {
                const attributeKey = priorityTypeToFilterKey[criteriaType] || criteriaType;
                const attribute = companyAttributeList[attributeKey];

                return {
                    id: criteriaType,
                    data: {
                        id: criteriaType,
                        title: attribute?.name,
                        iconName: attribute?.iconName,
                    },
                } as DraggableItem<PriorityAttribute>;
            });

            const resultingPriority1 =
                priorityToAdd === 1 ? [...filteredPriority1, ...newItems] : filteredPriority1;
            const resultingPriority2 =
                priorityToAdd === 2 ? [...filteredPriority2, ...newItems] : filteredPriority2;
            const resultingPriority3 =
                priorityToAdd === 3 ? [...filteredPriority3, ...newItems] : filteredPriority3;

            setValue(SelectEvaluatorsFormFields.priority1, resultingPriority1);
            setValue(SelectEvaluatorsFormFields.priority2, resultingPriority2);
            setValue(SelectEvaluatorsFormFields.priority3, resultingPriority3);
            markEdited();

            if (hasAnyPriority(resultingPriority1, resultingPriority2, resultingPriority3)) {
                setPriorityMessageKey(undefined);
            }

            setIsAddCriteriaOpen(false);
        },
        [
            companyAttributeList,
            setValue,
            markEdited,
            priority1,
            priority2,
            priority3,
            getItemPriorityType,
            priorityToAdd,
        ],
    );

    const { cancelPendingAutosave, restorePendingAutosave } = useAutosaveEvaluatorSelection({
        isReadOnly,
        isHydrated: isFormInitialized,
        hasUserEdited,
        cycleEvaluationId,
        storedSelection: cycleEvaluation?.cycleEvaluatorSelection,
        cycleEvaluatorCriterias: buildCycleEvaluatorCriterias({
            priorityItems: { priority1, priority2, priority3 },
            cycleEvaluatorSelectionId: cycleEvaluatorSelectionId ?? '',
            criteriaIdToTypeMap,
        }),
        suggestedReviewerCount: formValues[SelectEvaluatorsFormFields.suggestedReviewerCount],
    });

    const onNext = async () => {
        if (isReadOnly) {
            return false;
        }

        // A launched cycle whose selection was never touched has nothing to validate or write:
        // "Next" merely moves on, the same as any other untouched step.
        if (isLaunched && !hasUserEdited) {
            return true;
        }

        const isValid = await trigger();

        if (!isValid) {
            const priorityFieldState = getFieldState(SelectEvaluatorsFormFields.priority1);
            const countFieldState = getFieldState(
                SelectEvaluatorsFormFields.suggestedReviewerCount,
            );

            setPriorityMessageKey(validationMessageKey(priorityFieldState.error?.message));
            setCountMessageKey(validationMessageKey(countFieldState.error?.message));

            return false;
        }

        setPriorityMessageKey(undefined);
        setCountMessageKey(undefined);

        const formData = getValues();

        if (!cycleEvaluatorSelectionId || !cycleEvaluationId) {
            handleError({});
            return false;
        }

        const cycleEvaluatorCriterias = buildCycleEvaluatorCriterias({
            priorityItems: {
                priority1: formData.priority1,
                priority2: formData.priority2,
                priority3: formData.priority3,
            },
            cycleEvaluatorSelectionId,
            criteriaIdToTypeMap,
        });

        cancelPendingAutosave({
            cycleEvaluatorCriterias,
            suggestedReviewerCount: formData.suggestedReviewerCount,
        });

        try {
            await updateCycleEvaluatorSelection({
                id: cycleEvaluatorSelectionId,
                cycleEvaluationId,
                cycleEvaluatorCriterias,
                suggestedReviewerCount: formData.suggestedReviewerCount,
            });

            // A draft's resume point moves on; a launched cycle has none left to move.
            if (cycleEvaluationGroupId && group && !isLaunched) {
                await updateCycleEvaluationGroup({
                    id: cycleEvaluationGroupId,
                    payload: buildCycleEvaluationGroupUpdatePayload({
                        group,
                        currentStep: CycleEvaluationGroupSteps.DEADLINES,
                    }),
                });
            }

            return true;
        } catch (error) {
            restorePendingAutosave();
            handleError({ error });
            return false;
        }
    };

    useFetchErrorsHandler({
        errors: [isCycleEvaluationError, isCompanyError],
    });

    useRegisterPageAction(
        cycleEvaluationGroupId ? feedback360SettingsOnNextAtom : selectEvaluatorsOnNextAtom,
        onNext,
    );

    if (isCycleEvaluationLoading || isCompanyLoading || !isFormInitialized) {
        return null;
    }

    const blockingMessages = [priorityMessageKey, countMessageKey]
        .filter((key): key is TranslationKey => Boolean(key))
        .map((key) => t(key));

    return (
        <>
            <SelectEvaluators
                isReadOnly={isReadOnly}
                evaluatorsCount={formValues[SelectEvaluatorsFormFields.suggestedReviewerCount]}
                priority1={priority1}
                priority2={priority2}
                priority3={priority3}
                blockingMessages={blockingMessages}
                onEvaluatorsCountChange={onEvaluatorsCountChange}
                onPriority1Change={onPriority1Change}
                onPriority2Change={onPriority2Change}
                onPriority3Change={onPriority3Change}
                onAddAttribute={onAddAttribute}
                onRemoveAttribute={onRemoveAttribute}
                showIntroHeader={!cycleEvaluationGroupId}
            />

            <AddCriteriaModal
                isOpen={isAddCriteriaOpen}
                criteriaOptions={criteriaOptions}
                criteriaSelected={selectedCriteria}
                onUpdateCriteria={onUpdateCriteria}
                onClose={() => setIsAddCriteriaOpen(false)}
            />
        </>
    );
};
